import { onBeforeUnmount, onMounted } from 'vue';

/**
 * Keeps a screen current without the user reaching for reload.
 *
 * Polling, not push: the backend has no push channel, and for a list a
 * few operators watch, a request every 20 seconds costs next to nothing.
 * The timer skips its tick while the tab is hidden, so a forgotten
 * background tab does not keep hitting the API, and it catches up the
 * moment the tab is looked at again (visibility or window focus), which
 * is when a stale list would actually be noticed.
 *
 * `refresh` must be quiet: no spinner, no reset of what the user is doing
 * on the screen. It is never run twice at once; a tick that lands while
 * the previous refresh is still out is dropped.
 *
 * Call from a component's setup; the listeners go with the component.
 */
export function useLiveRefresh(refresh: () => unknown, intervalMs = 20_000): void {
    let timer: ReturnType<typeof setInterval> | null = null;
    let running = false;
    let lastRun = Date.now();

    function run() {
        if (running || document.visibilityState !== 'visible') return;
        running = true;
        lastRun = Date.now();
        Promise.resolve()
            .then(refresh)
            .catch(() => { /* the next tick tries again */ })
            .finally(() => { running = false; });
    }

    // Focus and visibilitychange usually fire together when switching
    // back to the tab; the short guard turns that into one request.
    function onReturn() {
        if (Date.now() - lastRun > 2000) run();
    }

    onMounted(() => {
        timer = setInterval(run, intervalMs);
        document.addEventListener('visibilitychange', onReturn);
        window.addEventListener('focus', onReturn);
    });

    onBeforeUnmount(() => {
        if (timer) clearInterval(timer);
        document.removeEventListener('visibilitychange', onReturn);
        window.removeEventListener('focus', onReturn);
    });
}
